import { useAccelerateLabel } from "@/hooks/useAccelerateLabel";
import { useEscape } from "@/hooks/useKeyboardShortcut";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { cn } from "@/lib/utils";
import {
  chemstyleBonusesSelector,
  contentLabelsSelector,
} from "@/redux/loadedContent/loadedContentSlice";
import { chemStylesToIcon } from "@/redux/squadBuilder/squadBuilderUtils";
import { useTranslation } from "@repo/shared/i18n";
import { Chemistry as ChemistryValue, MetaRating } from "@repo/shared/types";
import {
  ChemStyleAccelerateGroup,
  allChemStyles,
  groupChemStylesByAccelerate,
} from "@repo/shared/utils";
import { FlaskConical } from "lucide-react";
import { ReactNode, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";

import { BottomSheetView } from "../BottomSheet/BottomSheetView";
import { IconButton } from "../IconButton/IconButton";
import { SelectableRow } from "../SelectableRow/SelectableRow";
import CloseButton from "../SpecificButtons/CloseButton/CloseButton";
import { ChemistryIcon } from "./Chemistry";

const CHEMISTRY_LEVELS: ChemistryValue[] = [0, 1, 2, 3];

/** What the accelerate grouping needs off the player being configured. */
export type ChemistrySelectPlayer = {
  preferredPosition: string;
  /** Face-card PAC…PHY. */
  attributes: number[];
  inGameAttributes?: Record<string, number>;
  height?: number;
  gender?: "MALE" | "FEMALE";
};

export type ChemistrySelectPanelProps = {
  player: ChemistrySelectPlayer;
  chemistry: ChemistryValue;
  /** null = no chemstyle picked yet. */
  chemStyleId: number | null;
  onChemistryChange: (chemistry: ChemistryValue) => void;
  /** Picking a style also closes the panel (chemistry picks keep it open). */
  onChemStyleChange: (chemStyleId: number) => void;
  /**
   * Starred style. Omit when `metaRatings` + `playerRoleId` are given — the
   * panel then derives it (`isBestChemstyleAtChem` at the selected chemistry;
   * at 0 chem, at full chem instead). Pass only to override.
   */
  bestChemStyleId?: number;
  /**
   * The player's role × chemstyle × chemistry meta ratings. When BOTH this
   * and `playerRoleId` are given, every chemstyle row shows the player's meta
   * rating with that style at the currently selected chemistry.
   */
  metaRatings?: MetaRating[];
  /** The role the shown meta ratings are graded under. */
  playerRoleId?: number;
  title?: string;
  subtitle?: ReactNode;
  /** Positions the trigger+panel wrapper (e.g. absolute placement on a card). */
  className?: string;
  /** Which trigger edge the panel grows from. */
  align?: "left" | "right";
  /**
   * "panel" (default) anchors the panel to the trigger — desktop. "sheet"
   * opens a BottomSheet — mobile / inside other sheets.
   */
  presentation?: "panel" | "sheet";
};

/**
 * The chemstyles a player can take, grouped by the AcceleRATE each gives them
 * (the DetailedPlayer chemstyle section's grouping, shared via
 * `groupChemStylesByAccelerate`). Falls back to one ungrouped list while the
 * player's in-game stats haven't arrived yet (compare cards before the
 * detailed fetch enriches them).
 */
const useAccelerateGroups = (
  player: ChemistrySelectPlayer,
  chemistry: ChemistryValue,
): ChemStyleAccelerateGroup[] => {
  const chemstyleBonuses = useSelector(chemstyleBonusesSelector);
  const { t } = useTranslation();
  const isGk = player.preferredPosition === "GK";

  if (player.inGameAttributes && player.height != null) {
    const groups = groupChemStylesByAccelerate({
      isGk,
      chemistry,
      inGameAttributes: player.inGameAttributes,
      height: player.height,
      gender: player.gender ?? "MALE",
      chemstyleBonuses,
    });
    if (groups.length > 0) return groups;
  }

  return [
    { accelerate: t("chemistryStyles"), chemStyles: allChemStyles(isGk) },
  ];
};

/**
 * The player's meta rating with a chemstyle at the selected chemistry, graded
 * under `playerRoleId` — undefined (no rating shown) unless both `metaRatings`
 * and `playerRoleId` are provided and the combination exists in the list.
 * Entries without their own `playerRoleId` (single-role responses) match any.
 *
 * Zero chem has a single rating — the basic (250) one, since no style applies
 * bonuses — so every style shows that value there.
 */
const chemStyleRating = (
  metaRatings: MetaRating[] | undefined,
  playerRoleId: number | undefined,
  chemistry: ChemistryValue,
  chemStyleId: number,
): string | undefined => {
  if (!metaRatings || playerRoleId == null) return undefined;
  const lookupStyleId = chemistry === 0 ? 250 : chemStyleId;
  return metaRatings
    .find(
      (m) =>
        m.chemstyleId === lookupStyleId &&
        m.chemistry === chemistry &&
        (m.playerRoleId == null || m.playerRoleId === playerRoleId),
    )
    ?.metaRating?.toFixed(1);
};

/**
 * The starred style: `isBestChemstyleAtChem` at the selected chemistry under
 * the graded role. At zero chem every style rates the same, so "best" is
 * judged at full chem instead — the style you'd want once the player gets
 * chemistry.
 */
const bestChemStyleAt = (
  metaRatings: MetaRating[] | undefined,
  playerRoleId: number | undefined,
  chemistry: ChemistryValue,
): number | undefined => {
  if (!metaRatings || playerRoleId == null) return undefined;
  const chem = chemistry === 0 ? 3 : chemistry;
  return metaRatings.find(
    (m) =>
      m.chemistry === chem &&
      m.isBestChemstyleAtChem &&
      (m.playerRoleId == null || m.playerRoleId === playerRoleId),
  )?.chemstyleId;
};

/**
 * The panel body always fills 3 columns: each group's span is its share of
 * them (1 group → all 3, 2 groups → the bigger one takes 2, 3 groups → 1
 * each), and the styles wrap into as many internal columns as the span.
 * Literal class strings so Tailwind generates them.
 */
const spanClasses: Record<number, { span: string; cols: string }> = {
  1: { span: "col-span-1", cols: "grid-cols-1" },
  2: { span: "col-span-2", cols: "grid-cols-2" },
  3: { span: "col-span-3", cols: "grid-cols-3" },
};

const groupSpans = (groups: ChemStyleAccelerateGroup[]): number[] => {
  if (groups.length === 1) return [3];
  if (groups.length === 2) {
    return groups[0].chemStyles.length >= groups[1].chemStyles.length
      ? [2, 1]
      : [1, 2];
  }
  return groups.map(() => 1);
};

const ChemistryLevelRow = ({
  chemistry,
  onChemistryChange,
}: Pick<ChemistrySelectPanelProps, "chemistry" | "onChemistryChange">) => (
  <div className="flex items-center gap-2">
    {CHEMISTRY_LEVELS.map((level) => (
      <button
        key={level}
        type="button"
        aria-label={`${level} chemistry`}
        aria-pressed={level === chemistry}
        onClick={() => onChemistryChange(level)}
        className={cn(
          "flex h-10 w-14 items-center justify-center rounded-[10px] transition-colors",
          level === chemistry
            ? "bg-blue-900/50 shadow-[inset_0_0_0_1px_var(--color-primary-border)]"
            : "bg-neutral-750 hover:bg-neutral-700",
        )}
      >
        <ChemistryIcon chemistry={level} className="w-6" />
      </button>
    ))}
  </div>
);

const ChemStyleRow = ({
  id,
  name,
  selected,
  best,
  rating,
  onSelect,
}: {
  id: number;
  name: string;
  selected: boolean;
  best: boolean;
  /** Pre-formatted meta rating; the best style keeps its star in front. */
  rating?: string;
  onSelect: () => void;
}) => (
  <SelectableRow
    label={name}
    icon={
      <span
        className="text-lg leading-none"
        style={{ fontFamily: "UltimateTeamIcons" }}
      >
        {chemStylesToIcon[id as keyof typeof chemStylesToIcon]}
      </span>
    }
    rating={best ? (rating ? `★ ${rating}` : "★") : rating}
    selected={selected}
    onSelect={onSelect}
  />
);

/**
 * The sheet body of the chemistry picker: title row with close button, the
 * four chemistry-level buttons and the chemstyles grouped by AcceleRATE.
 * Rendered by ChemistrySelectPanel's "sheet" presentation, and reusable
 * standalone inside other sheet chromes (e.g. the mobile compare view's
 * in-frame sheet).
 */
export const ChemistrySelectSheetContent = ({
  player,
  chemistry,
  chemStyleId,
  onChemistryChange,
  onPick,
  onClose,
  bestChemStyleId,
  metaRatings,
  playerRoleId,
  title,
  subtitle,
}: Pick<
  ChemistrySelectPanelProps,
  | "player"
  | "chemistry"
  | "chemStyleId"
  | "onChemistryChange"
  | "bestChemStyleId"
  | "metaRatings"
  | "playerRoleId"
  | "title"
  | "subtitle"
> & {
  /**
   * Called with the picked style; the host closes the sheet. Standalone hosts
   * should mirror ChemistrySelectPanel's rule: picking a style at 0 chem also
   * snaps chemistry to 3.
   */
  onPick: (chemStyleId: number) => void;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const accelerateLabel = useAccelerateLabel();
  const groups = useAccelerateGroups(player, chemistry);
  const { chemstyles } = useSelector(contentLabelsSelector);
  const starredChemStyleId =
    bestChemStyleId ?? bestChemStyleAt(metaRatings, playerRoleId, chemistry);

  return (
    // Bottom ad reserve: the sheet scrolls to its end, so without this padding
    // the last rows sit under the anchored ad.
    <div className="flex flex-col gap-4 px-6 py-2 ad-gutter-pad">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[17px] font-semibold text-neutral-100">
            {title ?? t("chemistrySelectTitle")}
          </div>
          {subtitle && (
            <div className="mt-[3px] text-[12.5px] text-foreground-muted">
              {subtitle}
            </div>
          )}
        </div>
        <CloseButton className="shrink-0" onClick={onClose} />
      </div>
      <ChemistryLevelRow
        chemistry={chemistry}
        onChemistryChange={onChemistryChange}
      />
      {groups.map((group) => (
        <div key={group.accelerate}>
          <div className="mb-2 text-xs font-semibold tracking-[0.06em] text-foreground-muted">
            {accelerateLabel(group.accelerate)}
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {group.chemStyles.map(({ id }) => (
              <ChemStyleRow
                key={id}
                id={id}
                name={chemstyles[id]?.name ?? ""}
                selected={id === chemStyleId}
                best={id === starredChemStyleId}
                rating={chemStyleRating(
                  metaRatings,
                  playerRoleId,
                  chemistry,
                  id,
                )}
                onSelect={() => onPick(id)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * The Select Chemistry & Chemistry Style picker: the compare ChemistryChip
 * as trigger, opening the four chemistry levels and every applicable
 * chemstyle grouped by the AcceleRATE it gives the player (RoleSelectPanel's
 * chrome). Picking a chemistry keeps it open; picking a style closes it.
 */
export const ChemistrySelectPanel = ({
  player,
  chemistry,
  chemStyleId,
  onChemistryChange,
  onChemStyleChange,
  bestChemStyleId,
  metaRatings,
  playerRoleId,
  title,
  subtitle,
  className,
  align = "left",
  presentation = "panel",
}: ChemistrySelectPanelProps) => {
  const [open, setOpen] = useState(false);
  const ref = useOutsideClick(() => setOpen(false));
  useEscape(() => setOpen(false));
  const { t } = useTranslation();
  const accelerateLabel = useAccelerateLabel();
  const groups = useAccelerateGroups(player, chemistry);
  const { chemstyles } = useSelector(contentLabelsSelector);
  const starredChemStyleId =
    bestChemStyleId ?? bestChemStyleAt(metaRatings, playerRoleId, chemistry);

  const pickStyle = (id: number) => {
    // A chemstyle does nothing at 0 chem — picking one implies wanting it
    // applied, so snap to full chemistry.
    if (chemistry === 0) onChemistryChange(3);
    onChemStyleChange(id);
  };

  // Same square/bordered/dark format as the CloseButton family; the flask
  // reads as "mix chemistry".
  const trigger = (
    <IconButton
      aria-label={t("selectChemistryAndChemstyle")}
      onClick={() => setOpen((prev) => !prev)}
      icon={<FlaskConical className="size-4 text-neutral-100" />}
    />
  );

  if (presentation === "sheet") {
    // Portaled to <body>: when the trigger lives inside another BottomSheet, a
    // transformed ancestor (translate-y) would otherwise become the containing
    // block for our fixed sheet and trap it there.
    return (
      <div className={className}>
        {trigger}
        {createPortal(
          <BottomSheetView
            isOpen={open}
            onClose={() => setOpen(false)}
            className="h-[85dvh]"
          >
            <ChemistrySelectSheetContent
              player={player}
              chemistry={chemistry}
              chemStyleId={chemStyleId}
              onChemistryChange={onChemistryChange}
              onPick={pickStyle}
              onClose={() => setOpen(false)}
              bestChemStyleId={bestChemStyleId}
              metaRatings={metaRatings}
              playerRoleId={playerRoleId}
              title={title}
              subtitle={subtitle}
            />
          </BottomSheetView>,
          document.body,
        )}
      </div>
    );
  }

  return (
    <div className={cn("relative", className)} ref={ref}>
      {trigger}
      {open && (
        <div
          className={cn(
            "absolute top-[calc(100%+10px)] z-60 flex max-h-[72vh] w-[min(760px,88vw)] flex-col overflow-hidden",
            align === "left" ? "left-0" : "right-0",
            "rounded-2xl border border-neutral-700 bg-neutral-800",
            "shadow-[0_24px_60px_rgba(0,0,0,0.6)]",
            "transition-all duration-150 ease-out",
            "translate-y-0 scale-100 opacity-100",
            "starting:-translate-y-1.5 starting:scale-[0.985] starting:opacity-0",
          )}
        >
          {/* Sticky header */}
          <div className="flex items-start justify-between gap-4 border-b border-neutral-700 px-5 pb-3.5 pt-[18px]">
            <div>
              <div className="text-[17px] font-semibold text-neutral-100">
                {title ?? t("chemistrySelectTitle")}
              </div>
              {subtitle && (
                <div className="mt-[3px] text-[12.5px] text-foreground-muted">
                  {subtitle}
                </div>
              )}
            </div>
            <div className="-mr-1 -mt-1 shrink-0">
              <CloseButton onClick={() => setOpen(false)} />
            </div>
          </div>

          {/* Scroll body */}
          <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-[18px] pt-4">
            <ChemistryLevelRow
              chemistry={chemistry}
              onChemistryChange={onChemistryChange}
            />

            <div className="mt-4 grid grid-cols-3 gap-x-4 gap-y-3.5">
              {groups.map((group, groupIdx) => {
                const span = spanClasses[groupSpans(groups)[groupIdx]];
                return (
                  <div
                    key={group.accelerate}
                    className={cn("min-w-0", span.span)}
                  >
                    <div className="mb-2 text-xs font-semibold tracking-[0.06em] text-foreground-muted">
                      {accelerateLabel(group.accelerate)}
                    </div>
                    <div className={cn("grid gap-1.5", span.cols)}>
                      {group.chemStyles.map(({ id }) => (
                        <ChemStyleRow
                          key={id}
                          id={id}
                          name={chemstyles[id]?.name ?? ""}
                          selected={id === chemStyleId}
                          best={id === starredChemStyleId}
                          rating={chemStyleRating(
                            metaRatings,
                            playerRoleId,
                            chemistry,
                            id,
                          )}
                          onSelect={() => pickStyle(id)}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
